import { entries, groupBy, min, sortBy } from 'lodash-es'
import { DateTime } from 'luxon'

import { WORLD_LEVELS, type WorldLevel } from '@/queries/constants'
import type { UserContextType } from '@/client/context/internal'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'
import { isUserInChannel } from '@/client/services/worlds'

import type { ChannelBase, ReactionDetails, WorldDetails } from './types'

export function getProcessedReactions({
  reactions,
}: {
  reactions: ReactionDetails[]
}): { reaction: string; reactionUsers: ReactionDetails[] }[] {
  const reactionsAndUsers = entries(groupBy(reactions, item => item.reaction))

  return sortBy(reactionsAndUsers, ([, reactionUsers]) =>
    min(reactionUsers.map(({ createdAt }) => createdAt))
  ).map(([reaction, reactionUsers]) => ({
    reaction,
    reactionUsers: sortBy(reactionUsers, item => item.createdAt, 'desc'),
  }))
}

export function getReactionListWithNewReaction({
  reactions,
  newReaction,
  userId,
  userDisplayName,
}: {
  reactions: { reaction: string; reactionUsers: ReactionDetails[] }[]
  newReaction: string
  userId: number
  userDisplayName: string
}): { reaction: string; reactionUsers: ReactionDetails[] }[] {
  const reactionAlreadyExists = reactions.some(
    ({ reaction: reactionUser }) => reactionUser === newReaction
  )

  if (!reactionAlreadyExists) {
    return [
      ...reactions,
      {
        reaction: newReaction,
        reactionUsers: [
          {
            createdAt: DateTime.now(),
            reaction: newReaction,
            user: {
              id: userId,
              displayName: userDisplayName,
            },
          },
        ],
      },
    ]
  }

  return reactions.map(({ reaction, reactionUsers }) => {
    if (reaction !== newReaction) {
      return { reaction, reactionUsers }
    }

    const userHasReacted = reactionUsers.some(item => item.user.id === userId)

    if (userHasReacted) {
      return {
        reaction,
        reactionUsers: reactionUsers.filter(item => item.user.id !== userId),
      }
    }

    return {
      reaction,
      reactionUsers: [
        ...reactionUsers,
        {
          createdAt: DateTime.now(),
          reaction: newReaction,
          user: {
            id: userId,
            displayName: userDisplayName,
          },
        },
      ],
    }
  })
}

export function canUserPostInChannel({
  user,
  channel,
  worldId,
}: {
  user: UserContextType
  channel: ChannelBase
  worldId: number
}): boolean {
  return (
    isUserInChannel({ channelId: channel.id, user }) &&
    (channel.type !== 'announcement' ||
      hasUserAuthScope(user, 'world:announcements:create', worldId))
  )
}

export function getChannelsUserCanPostIn({
  user,
  world,
}: {
  user: UserContextType
  world: Pick<WorldDetails, 'id' | 'slug' | 'channels'>
}): ChannelBase[] {
  return world.channels.filter(channel =>
    canUserPostInChannel({ user, channel, worldId: world.id })
  )
}

export function getAllowedLevels(minimumLevel: WorldLevel | null): WorldLevel[] {
  if (minimumLevel == null) {
    return [...WORLD_LEVELS]
  }

  const minimumLevelIndex = minimumLevel == null ? 0 : WORLD_LEVELS.indexOf(minimumLevel)

  return WORLD_LEVELS.slice(minimumLevelIndex)
}
